import React, {
  useEffect,
  useState,
  useCallback,
  ReactElement,
  FC,
} from 'react'
import { useFormikContext } from 'formik'
import { Oval } from 'react-loader-spinner'
import { PaymentFormState } from '@mindfulchefuk/features/GiftCards/interfaces'

import {
  Grid,
  Box,
  baseInputStyles,
  inputReadOnlyStyles,
  inputFocusStyles,
  inputInvalidStyles,
  inputPlaceholderStyles,
  Flex,
  ButtonLink,
  Icon,
  theme,
  colors,
} from '@mindfulchefuk/design-system'
import { BoxProps } from '@mindfulchefuk/design-system/layout/Box'
import useScript from '@mindfulchefuk/hooks/useScript'

import AmericanExpress from '@mindfulchefuk/components/icons/logos/AmericanExpress'
import DinersClub from '@mindfulchefuk/components/icons/logos/DinersClub'
import Discover from '@mindfulchefuk/components/icons/logos/Discover'
import Jcb from '@mindfulchefuk/components/icons/logos/Jcb'
import Maestro from '@mindfulchefuk/components/icons/logos/Maestro'
import Mastercard from '@mindfulchefuk/components/icons/logos/MasterCard'
import Visa from '@mindfulchefuk/components/icons/logos/Visa'

import {
  CC_CARD_NUMBER,
  CC_CVV,
  CC_EXPIRY_DATE,
  CC_ACTIVE,
  CC_ERROR,
  CC_VALID,
  CC_LOCKED,
} from '@mindfulchefuk/constants'

import { CardField } from '@mindfulchefuk/forms/CardField/CardField'
import {
  CardTokenisedType,
  CardValidationChangedType,
  FrameFocusType,
  FrameValidationChangedType,
  PaymentMethodChangedType,
  FormStateType,
} from './CreditCardInput.types'

const cardTypeLookup: { [key: string]: FC<BoxProps> } = {
  'American Express': AmericanExpress,
  'Diners Club': DinersClub,
  Discover,
  Jcb,
  Maestro,
  Mastercard,
  Visa,
}

const shouldUsePreviousState = (prevState: FormStateType, element: string) =>
  prevState[element] === CC_ERROR ||
  prevState[element] === CC_VALID ||
  prevState[element] === CC_LOCKED

const cardStyles = {
  base: baseInputStyles,
  readOnly: inputReadOnlyStyles,
  focus: inputFocusStyles,
  invalid: inputInvalidStyles,
  placeholder: inputPlaceholderStyles,
}

type TCreditCardInput = {
  onClear?: () => void
  onValidationError?: (error: string) => void
}

export const CreditCardInput = ({
  onClear,
  onValidationError,
}: TCreditCardInput): ReactElement => {
  const { setFieldValue, values, isSubmitting } = useFormikContext()

  const [showLoader, setShowLoader] = useState<boolean>(true)
  const [formState, setFormState] = useState<FormStateType>({
    [CC_CARD_NUMBER]: '',
    [CC_CVV]: '',
    [CC_EXPIRY_DATE]: '',
  })
  const [cardType, setCardType] = useState<string>('')
  const [showCardIcon, setShowCardIcon] = useState<boolean>(false)
  const [formIsValid, setFormIsValid] = useState<boolean>(false)
  const [loaded, error] = useScript(
    'https://cdn.checkout.com/js/framesv2.min.js'
  )

  const initialise = useCallback(() => {
    window.Frames.init({
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      cardSubmitted() {},
      cardTokenized({ token }: CardTokenisedType) {
        setFieldValue('paymentToken', token)
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      cardTokenizationFailed() {},
      cardValidationChanged({ isValid }: CardValidationChangedType) {
        setFormIsValid(isValid)

        if (isValid) {
          setFormState({
            [CC_CARD_NUMBER]: CC_LOCKED,
            [CC_CVV]: CC_LOCKED,
            [CC_EXPIRY_DATE]: CC_LOCKED,
          })

          window.Frames.submitCard()
            .then(() => {
              // added to prevent the customer from editing card details
              // once the token has been recieved.
              window.focus()
            })
            .catch((err) => {
              console.error(err)
            })
        }
      },
      frameFocus({ element }: FrameFocusType) {
        setFormState((prevState) => {
          if (shouldUsePreviousState(prevState, element)) {
            return prevState
          }

          return {
            ...prevState,
            [element]: CC_ACTIVE,
          }
        })
      },
      frameBlur({ element }: FrameFocusType) {
        setFormState((prevState) => {
          if (shouldUsePreviousState(prevState, element)) {
            return prevState
          }

          return {
            ...prevState,
            [element]: '',
          }
        })
      },
      frameValidationChanged({ element, isValid }: FrameValidationChangedType) {
        if (isValid) {
          setFormState((prevState) => {
            return {
              ...prevState,
              [element]: CC_VALID,
            }
          })

          if (element === CC_CARD_NUMBER) {
            setShowCardIcon(true)
          }
        } else {
          setFormState((prevState) => {
            return {
              ...prevState,
              [element]: CC_ERROR,
            }
          })

          if (element === CC_CARD_NUMBER) {
            setShowCardIcon(false)
          }
        }
      },
      frameActivated() {
        setShowLoader(false)
      },
      paymentMethodChanged({ paymentMethod }: PaymentMethodChangedType) {
        setCardType(paymentMethod)
      },
      publicKey: `Bearer ${process.env.NEXT_PUBLIC_CHECKOUT_PUBLIC_KEY}`,
      style: cardStyles,
    })
  }, [setFieldValue])

  const resetForm = useCallback(() => {
    setShowLoader(true)
    setFormIsValid(false)
    setFormState({
      [CC_CARD_NUMBER]: '',
      [CC_CVV]: '',
      [CC_EXPIRY_DATE]: '',
    })
    setCardType('')
    setShowCardIcon(false)
    initialise()
  }, [
    setShowLoader,
    setFormIsValid,
    setFormState,
    setCardType,
    setShowCardIcon,
    initialise,
  ])

  // Initialise form once script has loaded
  useEffect(() => {
    if (loaded && !error) initialise()
  }, [loaded, error, initialise])

  const { paymentToken } = values as PaymentFormState

  // Reset form if payment token is null
  useEffect(() => {
    if (paymentToken === null) {
      resetForm()
      if (onClear) onClear()
    }
  }, [onClear, paymentToken, resetForm])

  const cardNumberError = formState[CC_CARD_NUMBER] === 'error'
  const cardExpDate = formState[CC_EXPIRY_DATE] === 'error'
  const cardCVVError = formState[CC_CVV] === 'error'

  useEffect(() => {
    if (onValidationError) {
      if (cardNumberError) onValidationError('invalid card number')
      if (cardExpDate) onValidationError('invalid expiry date')
      if (cardCVVError) onValidationError('invalid CVV')
    }
  }, [cardCVVError, cardNumberError, cardExpDate, onValidationError])

  const CardIcon = !!cardType && showCardIcon ? cardTypeLookup[cardType] : null

  return (
    <Box position="relative" bg="white" z={1}>
      {showLoader && (
        <Flex
          position="absolute"
          bg="white"
          left={0}
          right={0}
          top={0}
          bottom={0}
          align="center"
          justify="center"
        >
          <Oval width={64} height={64} color={colors['pepper-50']} />
        </Flex>
      )}
      <Grid templateColumns="1fr" gap={16}>
        <CardField
          label="Card number"
          frameId="card-number-frame"
          state={formState[CC_CARD_NUMBER]}
          icon={CardIcon && <CardIcon width={theme.space[48]} />}
        />
        <Grid gap={16} templateColumns={{ base: '1fr', md: '1fr 1fr' }}>
          <CardField
            label="Expiry date"
            frameId="expiry-date-frame"
            state={formState[CC_EXPIRY_DATE]}
          />
          <CardField
            label="Security code"
            frameId="cvv-frame"
            state={formState[CC_CVV]}
            icon={<Icon type="padlock" size={20} />}
          />
        </Grid>
      </Grid>
      {formIsValid && !isSubmitting && (
        <Box my={8}>
          <ButtonLink
            variant="body-sm"
            type="button"
            color="aubergine-400"
            onClick={() => {
              setFieldValue('paymentToken', null)
            }}
          >
            Clear payment details
          </ButtonLink>
        </Box>
      )}
    </Box>
  )
}
